import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Receipt } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";

const emptyItem = () => ({ description: "", qty: 1, unit_price: 0 });

/** Manual (ad-hoc) invoice dialog opened from the Customer detail drawer.
 *  Line items are freely editable; totals recompute live; office can save
 *  as a draft (default — sits in Pending Review) or send immediately.
 */
export function ManualInvoiceDialog({ open, onOpenChange, customer, onCreated }) {
    const [items, setItems] = useState([emptyItem()]);
    const [tax, setTax] = useState(0);
    const [dueDays, setDueDays] = useState(7);
    const [notes, setNotes] = useState("");
    const [sendNow, setSendNow] = useState(false);
    const [saving, setSaving] = useState(false);

    const reset = () => {
        setItems([emptyItem()]);
        setTax(0);
        setDueDays(7);
        setNotes("");
        setSendNow(false);
    };

    const setItem = (i, patch) => {
        setItems((cur) => cur.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
    };

    const addItem = () => setItems((cur) => [...cur, emptyItem()]);
    const removeItem = (i) =>
        setItems((cur) => (cur.length <= 1 ? cur : cur.filter((_, idx) => idx !== i)));

    const subtotal = items.reduce(
        (sum, it) => sum + (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0),
        0
    );
    const taxNum = parseFloat(tax) || 0;
    const total = subtotal + taxNum;

    const canSave = items.some(
        (it) => (it.description || "").trim() && (parseFloat(it.unit_price) || 0) > 0
    );

    const save = async () => {
        if (!customer) return;
        const cleanItems = items
            .filter((it) => (it.description || "").trim() && (parseFloat(it.unit_price) || 0) > 0)
            .map((it) => ({
                description: it.description.trim(),
                qty: parseFloat(it.qty) || 1,
                unit_price: parseFloat(it.unit_price) || 0,
            }));
        if (!cleanItems.length) {
            toast.error("Add at least one line item with a description and price");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/invoices", {
                customer_id: customer.id,
                items: cleanItems,
                tax: taxNum,
                due_in_days: parseInt(dueDays, 10) || 7,
                notes: notes.trim(),
                send_immediately: sendNow,
            });
            toast.success(
                sendNow
                    ? `Invoice ${data.invoice_number} sent (${fmtMoney(data.total)})`
                    : `Draft invoice ${data.invoice_number} created — review before sending`
            );
            reset();
            onCreated?.(data);
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not create invoice");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
            <SheetContent
                className="w-full sm:max-w-xl overflow-y-auto"
                data-testid="manual-invoice-sheet"
            >
                <SheetHeader>
                    <SheetTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
                        <Receipt className="w-5 h-5" />
                        Create invoice
                    </SheetTitle>
                    <SheetDescription>
                        {customer ? `For ${customer.name} · ${customer.account_number}` : ""}
                    </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-5">
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                                Line items
                            </Label>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={addItem}
                                data-testid="manual-invoice-add-item"
                            >
                                <Plus className="w-3.5 h-3.5 mr-1" />
                                Add line
                            </Button>
                        </div>
                        <div className="space-y-2">
                            {items.map((it, idx) => (
                                <div
                                    key={idx}
                                    className="grid grid-cols-12 gap-2 items-start"
                                    data-testid={`manual-invoice-item-${idx}`}
                                >
                                    <div className="col-span-6">
                                        <Input
                                            placeholder="Description (e.g. Extra bulk pickup)"
                                            value={it.description}
                                            onChange={(e) => setItem(idx, { description: e.target.value })}
                                            data-testid={`manual-invoice-desc-${idx}`}
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <Input
                                            type="number"
                                            step="0.5"
                                            min="0"
                                            placeholder="Qty"
                                            value={it.qty}
                                            onChange={(e) => setItem(idx, { qty: e.target.value })}
                                            data-testid={`manual-invoice-qty-${idx}`}
                                        />
                                    </div>
                                    <div className="col-span-3">
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            placeholder="Unit $"
                                            value={it.unit_price}
                                            onChange={(e) => setItem(idx, { unit_price: e.target.value })}
                                            data-testid={`manual-invoice-price-${idx}`}
                                        />
                                    </div>
                                    <div className="col-span-1 flex justify-end pt-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                                            onClick={() => removeItem(idx)}
                                            disabled={items.length <= 1}
                                            data-testid={`manual-invoice-remove-${idx}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs mb-1 block">Tax ($)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={tax}
                                onChange={(e) => setTax(e.target.value)}
                                data-testid="manual-invoice-tax"
                            />
                        </div>
                        <div>
                            <Label className="text-xs mb-1 block">Due in (days)</Label>
                            <Input
                                type="number"
                                min="0"
                                value={dueDays}
                                onChange={(e) => setDueDays(e.target.value)}
                                data-testid="manual-invoice-due-days"
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs mb-1 block">Notes / memo (optional)</Label>
                        <textarea
                            className="w-full min-h-[70px] rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Shows on the invoice PDF and customer email."
                            data-testid="manual-invoice-notes"
                        />
                    </div>

                    <div className="rounded-md border border-border p-3 space-y-1.5">
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Subtotal</span>
                            <span className="tabular">{fmtMoney(subtotal)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Tax</span>
                            <span className="tabular">{fmtMoney(taxNum)}</span>
                        </div>
                        <div className="flex justify-between text-base font-semibold pt-1 border-t border-border">
                            <span>Total</span>
                            <span className="tabular" data-testid="manual-invoice-total">
                                {fmtMoney(total)}
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center justify-between rounded-md border border-border p-3">
                        <div>
                            <div className="text-sm font-medium">Send immediately</div>
                            <div className="text-[11px] text-muted-foreground">
                                Off = save as draft in Pending Review · On = email/SMS the customer now
                            </div>
                        </div>
                        <Switch
                            checked={sendNow}
                            onCheckedChange={setSendNow}
                            data-testid="manual-invoice-send-now"
                        />
                    </div>

                    <div className="flex gap-2 pt-2">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => onOpenChange(false)}
                            data-testid="manual-invoice-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            className="flex-1"
                            onClick={save}
                            disabled={!canSave || saving}
                            data-testid="manual-invoice-save"
                        >
                            {saving ? "Saving…" : sendNow ? "Create & send" : "Save draft"}
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
